import { useEffect, useRef, useState } from 'react';
import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { publicClientConfig } from '@/config/public-client-config';
import { Sort } from '@/types/sort.type';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useSocketIo } from '@/context/socket-io.context';
import { generateOperationId } from '@/shared/operation-id';

const reviewSelectionOrchestrationLogger = createClientLogger({
    scope: 'review-selection-orchestration',
});

type UseReviewSelectionOrchestrationParams = {
    accessToken: string;
    selectionSessionId: string;
    filtersQuery: string[];
    sort: Sort;
    selectionReconcileRevision: number;
    setPickedReviews: (reviewIds: string[]) => void;
};

type ReviewSelectionOrchestration = {
    enqueueReviewSelectionMutation: <T>(mutation: () => Promise<T>) => Promise<T>;
    waitForReviewSelectionReconcile: () => Promise<void>;
    isReviewSelectionReconcileRunning: boolean;
    isReviewSelectionReconcileUiRunning: boolean;
    isReviewSelectionMutationRunning: boolean;
};

type ReconcileRequestOutcome = 'pending' | 'success' | 'canceled' | 'error';

type PendingReviewSelectionReconcileRecovery = {
    reconcileRunId: number;
    requestId: string;
    startedAtMs: number;
    timerId: ReturnType<typeof setTimeout> | null;
    abortController: AbortController | null;
};

export const useReviewSelectionOrchestration = ({
    accessToken,
    selectionSessionId,
    filtersQuery,
    sort,
    selectionReconcileRevision,
    setPickedReviews,
}: UseReviewSelectionOrchestrationParams): ReviewSelectionOrchestration => {
    const { reviewsApi } = useMarketplaceApi();
    const { events, isConnected } = useSocketIo();
    const [isReviewSelectionReconcileRunning, setIsReviewSelectionReconcileRunning] = useState(false);
    const [isReviewSelectionReconcileUiRunning, setIsReviewSelectionReconcileUiRunning] = useState(false);
    const [isReviewSelectionMutationRunning, setIsReviewSelectionMutationRunning] = useState(false);
    const reviewSelectionReconcilePromiseRef = useRef<Promise<void> | null>(null);
    const reviewSelectionReconcileRunIdRef = useRef(0);
    const reviewSelectionReconcileRequestSeedRef = useRef(0);
    const latestApplicableReconcileRequestIdRef = useRef<string | null>(null);
    const latestApplicableReconcileRequestOutcomeRef = useRef<ReconcileRequestOutcome | null>(null);
    const latestApplicableReconcileSocketCompletedRef = useRef(false);
    const latestApplicableReconcileFallbackResolvedRef = useRef(false);
    const reviewSelectionReconcileLocalWorkSettledRunIdRef = useRef<number | null>(null);
    const reviewSelectionReconcileRecoveryRef = useRef<PendingReviewSelectionReconcileRecovery | null>(null);
    const reviewSelectionMutationQueueRef = useRef<Promise<void>>(Promise.resolve());
    const pendingReviewSelectionMutationsRef = useRef(0);

    const createReconcileRequestId = () => {
        reviewSelectionReconcileRequestSeedRef.current += 1;
        return generateOperationId(reviewSelectionReconcileRequestSeedRef.current);
    };

    const clearPendingReviewSelectionReconcileRecovery = () => {
        const pendingRecovery = reviewSelectionReconcileRecoveryRef.current;
        if (!pendingRecovery) {
            return;
        }

        if (pendingRecovery.timerId) {
            clearTimeout(pendingRecovery.timerId);
        }

        pendingRecovery.abortController?.abort();
        reviewSelectionReconcileRecoveryRef.current = null;
    };

    const resetLatestApplicableReconcileTracking = () => {
        clearPendingReviewSelectionReconcileRecovery();
        latestApplicableReconcileRequestIdRef.current = null;
        latestApplicableReconcileRequestOutcomeRef.current = null;
        latestApplicableReconcileSocketCompletedRef.current = false;
        latestApplicableReconcileFallbackResolvedRef.current = false;
    };

    const isLatestApplicableReconcileRequest = (reconcileRunId: number, requestId: string) =>
        reviewSelectionReconcileRunIdRef.current === reconcileRunId &&
        latestApplicableReconcileRequestIdRef.current === requestId;

    const hasLatestApplicableReconcileRecoveryResolved = () =>
        latestApplicableReconcileSocketCompletedRef.current || latestApplicableReconcileFallbackResolvedRef.current;

    const shouldContinueLatestApplicableReconcileRecovery = (reconcileRunId: number, requestId: string) => {
        if (!isLatestApplicableReconcileRequest(reconcileRunId, requestId)) {
            return false;
        }

        if (reviewSelectionReconcileLocalWorkSettledRunIdRef.current !== reconcileRunId) {
            return false;
        }

        if (latestApplicableReconcileRequestOutcomeRef.current !== 'canceled') {
            return false;
        }

        return !hasLatestApplicableReconcileRecoveryResolved();
    };

    const maybeFinishReviewSelectionReconcileUiRun = (reconcileRunId: number) => {
        if (reviewSelectionReconcileRunIdRef.current !== reconcileRunId) {
            return;
        }

        if (reviewSelectionReconcileLocalWorkSettledRunIdRef.current !== reconcileRunId) {
            return;
        }

        const latestRequestOutcome = latestApplicableReconcileRequestOutcomeRef.current;
        if (!latestRequestOutcome || latestRequestOutcome === 'pending') {
            return;
        }

        if (
            latestRequestOutcome === 'success' ||
            latestRequestOutcome === 'error' ||
            hasLatestApplicableReconcileRecoveryResolved()
        ) {
            setIsReviewSelectionReconcileUiRunning(false);
        }
    };

    const markLatestApplicableReconcileRequest = (requestId: string) => {
        clearPendingReviewSelectionReconcileRecovery();
        latestApplicableReconcileRequestIdRef.current = requestId;
        latestApplicableReconcileRequestOutcomeRef.current = 'pending';
        latestApplicableReconcileSocketCompletedRef.current = false;
        latestApplicableReconcileFallbackResolvedRef.current = false;
    };

    const markLatestApplicableReconcileRequestOutcome = (
        reconcileRunId: number,
        requestId: string,
        outcome: ReconcileRequestOutcome,
    ) => {
        if (reviewSelectionReconcileRunIdRef.current !== reconcileRunId) {
            return;
        }

        if (latestApplicableReconcileRequestIdRef.current !== requestId) {
            return;
        }

        latestApplicableReconcileRequestOutcomeRef.current = outcome;
        maybeFinishReviewSelectionReconcileUiRun(reconcileRunId);
    };

    const markLatestApplicableReconcileFallbackResolved = (reconcileRunId: number, requestId: string) => {
        if (!isLatestApplicableReconcileRequest(reconcileRunId, requestId)) {
            return;
        }

        clearPendingReviewSelectionReconcileRecovery();
        latestApplicableReconcileFallbackResolvedRef.current = true;
        maybeFinishReviewSelectionReconcileUiRun(reconcileRunId);
    };

    const failLatestApplicableReconcileRecovery = (
        reconcileRunId: number,
        requestId: string,
        reason: 'timeout' | 'error',
        error?: unknown,
    ) => {
        if (!isLatestApplicableReconcileRequest(reconcileRunId, requestId)) {
            return;
        }

        clearPendingReviewSelectionReconcileRecovery();
        if (reason === 'error') {
            logClientError({
                logger: reviewSelectionOrchestrationLogger,
                message:
                    '[client.review-selection.reconcile-recovery] Failed to recover review selection reconcile state.',
                error,
                context: {
                    selectionSessionId,
                    requestId,
                },
            });
        } else {
            reviewSelectionOrchestrationLogger.warn(
                '[client.review-selection.reconcile] Review selection reconcile recovery timed out',
                {
                    selectionSessionId,
                    requestId,
                },
            );
        }

        markLatestApplicableReconcileRequestOutcome(reconcileRunId, requestId, 'error');
    };

    const scheduleLatestApplicableReconcileRecoveryPoll = (
        reconcileRunId: number,
        requestId: string,
        startedAtMs: number,
    ) => {
        if (!shouldContinueLatestApplicableReconcileRecovery(reconcileRunId, requestId)) {
            return;
        }

        clearPendingReviewSelectionReconcileRecovery();
        const timerId = setTimeout(() => {
            void pollLatestApplicableReconcileStatus(reconcileRunId, requestId, startedAtMs);
        }, publicClientConfig.reviewSelectionReconcileStatusPollIntervalMs);
        reviewSelectionReconcileRecoveryRef.current = {
            reconcileRunId,
            requestId,
            startedAtMs,
            timerId,
            abortController: null,
        };
    };

    const recoverLatestApplicableReconcileSelectionSession = (
        reconcileRunId: number,
        requestId: string,
        startedAtMs: number,
    ) => {
        if (!shouldContinueLatestApplicableReconcileRecovery(reconcileRunId, requestId)) {
            return;
        }

        if (Date.now() - startedAtMs >= publicClientConfig.reviewSelectionReconcileRequestTimeoutMs) {
            failLatestApplicableReconcileRecovery(reconcileRunId, requestId, 'timeout');
            return;
        }

        const abortController = new AbortController();
        let didTimeout = false;
        const remainingRecoveryMs = Math.max(
            1,
            publicClientConfig.reviewSelectionReconcileRequestTimeoutMs - (Date.now() - startedAtMs),
        );
        const timerId = setTimeout(() => {
            didTimeout = true;
            abortController.abort();
        }, remainingRecoveryMs);
        reviewSelectionReconcileRecoveryRef.current = {
            reconcileRunId,
            requestId,
            startedAtMs,
            timerId,
            abortController,
        };

        void reviewsApi
            .getReviewSelectionSession(selectionSessionId, {
                signal: abortController.signal,
                authToken: accessToken,
            })
            .then((response) => {
                if (!shouldContinueLatestApplicableReconcileRecovery(reconcileRunId, requestId)) {
                    return;
                }

                setPickedReviews(response.data.ids);
                markLatestApplicableReconcileFallbackResolved(reconcileRunId, requestId);
            })
            .catch((error) => {
                if (isRequestCanceled(error)) {
                    if (didTimeout) {
                        failLatestApplicableReconcileRecovery(reconcileRunId, requestId, 'timeout');
                    }
                    return;
                }

                failLatestApplicableReconcileRecovery(reconcileRunId, requestId, 'error', error);
            })
            .finally(() => {
                const pendingRecovery = reviewSelectionReconcileRecoveryRef.current;
                if (
                    pendingRecovery &&
                    pendingRecovery.reconcileRunId === reconcileRunId &&
                    pendingRecovery.requestId === requestId &&
                    pendingRecovery.abortController === abortController
                ) {
                    clearTimeout(timerId);
                    reviewSelectionReconcileRecoveryRef.current = null;
                }
            });
    };

    const pollLatestApplicableReconcileStatus = (reconcileRunId: number, requestId: string, startedAtMs: number) => {
        if (!shouldContinueLatestApplicableReconcileRecovery(reconcileRunId, requestId)) {
            return;
        }

        if (Date.now() - startedAtMs >= publicClientConfig.reviewSelectionReconcileRequestTimeoutMs) {
            failLatestApplicableReconcileRecovery(reconcileRunId, requestId, 'timeout');
            return;
        }

        const abortController = new AbortController();
        reviewSelectionReconcileRecoveryRef.current = {
            reconcileRunId,
            requestId,
            startedAtMs,
            timerId: null,
            abortController,
        };

        void reviewsApi
            .getReviewSelectionReconcileStatus(selectionSessionId, requestId, {
                signal: abortController.signal,
                authToken: accessToken,
            })
            .then((response) => {
                if (!shouldContinueLatestApplicableReconcileRecovery(reconcileRunId, requestId)) {
                    return;
                }

                if (response.data.status === 'completed') {
                    if (response.data.count !== response.data.ids.length) {
                        reviewSelectionOrchestrationLogger.warn(
                            '[client.review-selection.reconcile] Review selection reconcile HTTP completion count mismatch',
                            {
                                selectionSessionId,
                                requestId,
                                count: response.data.count,
                                idsLength: response.data.ids.length,
                            },
                        );
                    }

                    recoverLatestApplicableReconcileSelectionSession(reconcileRunId, requestId, startedAtMs);
                    return;
                }

                if (response.data.status === 'stale') {
                    recoverLatestApplicableReconcileSelectionSession(reconcileRunId, requestId, startedAtMs);
                    return;
                }

                scheduleLatestApplicableReconcileRecoveryPoll(reconcileRunId, requestId, startedAtMs);
            })
            .catch((error) => {
                if (isRequestCanceled(error)) {
                    return;
                }

                failLatestApplicableReconcileRecovery(reconcileRunId, requestId, 'error', error);
            })
            .finally(() => {
                const pendingRecovery = reviewSelectionReconcileRecoveryRef.current;
                if (
                    pendingRecovery &&
                    pendingRecovery.reconcileRunId === reconcileRunId &&
                    pendingRecovery.requestId === requestId &&
                    pendingRecovery.abortController === abortController &&
                    !pendingRecovery.timerId
                ) {
                    reviewSelectionReconcileRecoveryRef.current = null;
                }
            });
    };

    const maybeStartLatestApplicableReconcileRecovery = (reconcileRunId: number) => {
        if (reviewSelectionReconcileRunIdRef.current !== reconcileRunId) {
            return;
        }

        if (reviewSelectionReconcileLocalWorkSettledRunIdRef.current !== reconcileRunId) {
            return;
        }

        const requestId = latestApplicableReconcileRequestIdRef.current;
        if (!requestId || !shouldContinueLatestApplicableReconcileRecovery(reconcileRunId, requestId)) {
            return;
        }

        const pendingRecovery = reviewSelectionReconcileRecoveryRef.current;
        if (
            pendingRecovery &&
            pendingRecovery.reconcileRunId === reconcileRunId &&
            pendingRecovery.requestId === requestId
        ) {
            return;
        }

        if (isConnected) {
            return;
        }

        scheduleLatestApplicableReconcileRecoveryPoll(reconcileRunId, requestId, Date.now());
    };

    const enqueueReviewSelectionMutation = <T>(mutation: () => Promise<T>): Promise<T> => {
        pendingReviewSelectionMutationsRef.current += 1;
        setIsReviewSelectionMutationRunning(true);

        const queuedMutation = reviewSelectionMutationQueueRef.current.then(async () => {
            try {
                resetLatestApplicableReconcileTracking();
                setIsReviewSelectionReconcileUiRunning(false);
                return await mutation();
            } finally {
                pendingReviewSelectionMutationsRef.current = Math.max(
                    0,
                    pendingReviewSelectionMutationsRef.current - 1,
                );
                if (pendingReviewSelectionMutationsRef.current === 0) {
                    setIsReviewSelectionMutationRunning(false);
                }
            }
        });
        reviewSelectionMutationQueueRef.current = queuedMutation.then(() => undefined).catch(() => undefined);
        return queuedMutation;
    };

    const waitForReviewSelectionMutations = async () => {
        await reviewSelectionMutationQueueRef.current;
    };

    const waitForReviewSelectionReconcile = async () => {
        const reconcilePromise = reviewSelectionReconcilePromiseRef.current;
        if (!reconcilePromise) {
            return;
        }

        try {
            await reconcilePromise;
        } catch {
            // Reconcile failures are already handled in-place and should not block UI actions.
        }
    };

    useEffect(() => {
        if (!selectionSessionId) {
            return;
        }

        return events.on('reviewSelectionReconcileCompleted', (event) => {
            if (event.sessionId !== selectionSessionId) {
                return;
            }

            if (latestApplicableReconcileRequestIdRef.current !== event.requestId) {
                return;
            }

            setPickedReviews(event.ids);
            clearPendingReviewSelectionReconcileRecovery();
            latestApplicableReconcileSocketCompletedRef.current = true;
            maybeFinishReviewSelectionReconcileUiRun(reviewSelectionReconcileRunIdRef.current);
        });
    }, [events, selectionSessionId, setPickedReviews]);

    useEffect(() => {
        return () => {
            clearPendingReviewSelectionReconcileRecovery();
        };
    }, []);

    useEffect(() => {
        if (isConnected) {
            return;
        }

        maybeStartLatestApplicableReconcileRecovery(reviewSelectionReconcileRunIdRef.current);
    }, [isConnected]);

    // Selection reconcile contract:
    // 1) Trigger on filter/sort changes.
    // 2) Trigger on explicit revision bumps (e.g. answer/AI state updates).
    // 3) Run reconcile immediately for fast UI feedback unless a local selection mutation is already in flight.
    // 4) When mutations are pending, delay reconcile until the queue drains so stale pre-mutation requests cannot win.
    // 5) Ignore stale responses and apply intersection-only backend result.
    useEffect(() => {
        if (!selectionSessionId) {
            resetLatestApplicableReconcileTracking();
            reviewSelectionReconcileLocalWorkSettledRunIdRef.current = null;
            reviewSelectionReconcilePromiseRef.current = null;
            setIsReviewSelectionReconcileRunning(false);
            setIsReviewSelectionReconcileUiRunning(false);
            return;
        }

        const abortController = new AbortController();
        const reconcileTimeoutId = setTimeout(() => {
            abortController.abort();
        }, publicClientConfig.reviewSelectionReconcileRequestTimeoutMs);
        const reconcileRunId = reviewSelectionReconcileRunIdRef.current + 1;
        reviewSelectionReconcileRunIdRef.current = reconcileRunId;
        const shouldDelayReconcileUntilMutationQueueSettles = pendingReviewSelectionMutationsRef.current > 0;
        setIsReviewSelectionReconcileRunning(true);
        setIsReviewSelectionReconcileUiRunning(true);
        reviewSelectionReconcileLocalWorkSettledRunIdRef.current = null;
        resetLatestApplicableReconcileTracking();

        const runReconcile = (requestId: string) => {
            markLatestApplicableReconcileRequest(requestId);
            return reviewsApi.reconcileReviewSelectionSession(
                {
                    sessionId: selectionSessionId,
                    search: filtersQuery,
                    ...sort,
                    requestId,
                },
                { signal: abortController.signal, authToken: accessToken },
            );
        };

        const applyReconcileResponse = (
            requestId: string,
            response: Awaited<ReturnType<typeof reviewsApi.reconcileReviewSelectionSession>> | null,
        ) => {
            if (!response) {
                return;
            }

            if (reviewSelectionReconcileRunIdRef.current !== reconcileRunId) {
                return;
            }

            if (latestApplicableReconcileRequestIdRef.current !== requestId) {
                return;
            }

            setPickedReviews(response.data.ids);
        };

        const handleReconcileError = (error: unknown) => {
            if (isRequestCanceled(error)) {
                return;
            }

            logClientError({
                logger: reviewSelectionOrchestrationLogger,
                message: '[client.review-selection.reconcile] Failed to reconcile review selection.',
                error,
                context: {
                    selectionSessionId,
                },
            });
        };

        const runTrackedReconcileRequest = () => {
            const requestId = createReconcileRequestId();
            return runReconcile(requestId)
                .then((response) => {
                    markLatestApplicableReconcileRequestOutcome(reconcileRunId, requestId, 'success');
                    applyReconcileResponse(requestId, response);
                })
                .catch((error) => {
                    markLatestApplicableReconcileRequestOutcome(
                        reconcileRunId,
                        requestId,
                        isRequestCanceled(error) ? 'canceled' : 'error',
                    );
                    handleReconcileError(error);
                });
        };

        const fastReconcilePromise = shouldDelayReconcileUntilMutationQueueSettles
            ? waitForReviewSelectionMutations().then(() => {
                  if (abortController.signal.aborted || reviewSelectionReconcileRunIdRef.current !== reconcileRunId) {
                      return;
                  }

                  return runTrackedReconcileRequest();
              })
            : runTrackedReconcileRequest();

        const reconcilePromise = Promise.allSettled([fastReconcilePromise]).then((response) => {
            void response;
            reviewSelectionReconcileLocalWorkSettledRunIdRef.current = reconcileRunId;
            maybeFinishReviewSelectionReconcileUiRun(reconcileRunId);
            maybeStartLatestApplicableReconcileRecovery(reconcileRunId);
        });
        const trackedPromise = reconcilePromise.finally(() => {
            clearTimeout(reconcileTimeoutId);
            if (reviewSelectionReconcileRunIdRef.current === reconcileRunId) {
                reviewSelectionReconcilePromiseRef.current = null;
                setIsReviewSelectionReconcileRunning(false);
            }
        });
        reviewSelectionReconcilePromiseRef.current = trackedPromise;

        return () => {
            clearTimeout(reconcileTimeoutId);
            abortController.abort();
        };
    }, [accessToken, filtersQuery, selectionSessionId, sort.sortKey, sort.sortOrder, selectionReconcileRevision]);

    return {
        enqueueReviewSelectionMutation,
        waitForReviewSelectionReconcile,
        isReviewSelectionReconcileRunning,
        isReviewSelectionReconcileUiRunning,
        isReviewSelectionMutationRunning,
    };
};
